import {
  Mapping,
  ModelConceptClass,
  ModelConceptRelationship as ModelConceptRelationshipLabel,
  RelationshipMapping,
} from 'src/containers/ProjectDiagram/types/data';
import {
  Model,
  ModelSnapshot,
} from 'src/containers/ProjectDiagram/types/model';
import {
  isMapping,
  isRelationshipMapping,
} from 'src/containers/ProjectDiagram/utils/data';
import { isValidMapping } from 'src/containers/ProjectDiagram/utils/mapping/isValidMapping';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { getAllClassHierarchy } from 'src/utils/getAllClassHierarchy';

/**
 * Find all valid mappings for a ModelConceptClass.
 */
export const getValidMappingsForClass =
  (data: ModelConceptClass) =>
  (model: Model | ModelSnapshot): Mapping[] => {
    return model.linkDataArray.filter(
      (mapping): mapping is Mapping =>
        isMapping(mapping) &&
        isValidMapping(mapping)(model) &&
        mapping.to === data.key
    );
  };

/**
 * Find all invalid mappings for a ModelConceptClass.
 */
export const getInvalidMappingsForClass =
  (data: ModelConceptClass) =>
  (model: Model | ModelSnapshot): Mapping[] => {
    return model.linkDataArray.filter(
      (mapping): mapping is Mapping =>
        isMapping(mapping) &&
        !isValidMapping(mapping)(model) &&
        mapping.to === data.key
    );
  };

/**
 * Find all valid mappings for a ModelConceptRelationshipLabel.
 */
export const getValidMappingsForRelationship =
  (data: ModelConceptRelationshipLabel) =>
  (model: Model | ModelSnapshot): RelationshipMapping[] => {
    const fromIri = data.from && getModelConceptClassValue(data.from);
    const toIri = data.to && getModelConceptClassValue(data.to);
    if (!fromIri || !toIri) {
      return [];
    }

    const mappingsByKey = model.linkDataArray.reduce<Record<string, Mapping>>(
      (acc, linkData) => {
        if (isMapping(linkData)) {
          acc[linkData.key] = linkData;
        }
        return acc;
      },
      {}
    );

    const hierarchyByIri = getAllClassHierarchy(model);

    const isMappingEqualOrDescendant = (
      mappingKey: string,
      classKey?: string
    ): boolean => {
      const childClassIri = getModelConceptClassValue(
        mappingsByKey[mappingKey]?.to || ''
      );
      const parentClassIri = getModelConceptClassValue(classKey || '');
      if (!childClassIri || !parentClassIri) {
        return false;
      }
      if (childClassIri === parentClassIri) {
        return true;
      }

      return Boolean(
        hierarchyByIri[parentClassIri]?.descendants[childClassIri]
      );
    };

    return model.linkDataArray.filter(
      (mapping): mapping is RelationshipMapping =>
        Boolean(
          isRelationshipMapping(mapping) &&
            mapping.relationshipIri === data.value &&
            ((mapping.from &&
              isMappingEqualOrDescendant(mapping.from, data.from) &&
              mapping.toClassIri === toIri &&
              mapping.toPrimaryIdentifier?.parts.length) ||
              (mapping.to &&
                isMappingEqualOrDescendant(mapping.to, data.to) &&
                mapping.fromClassIri === fromIri &&
                mapping.fromPrimaryIdentifier?.parts.length))
        )
    );
  };

/**
 * Find all invalid mappings for a ModelConceptRelationshipLabel.
 */
export const getInvalidMappingsForRelationship =
  (data: ModelConceptRelationshipLabel) =>
  (model: Model | ModelSnapshot): RelationshipMapping[] => {
    const fromIri = data.from && getModelConceptClassValue(data.from);
    const toIri = data.to && getModelConceptClassValue(data.to);
    if (!fromIri || !toIri) {
      return [];
    }

    const mappingsByKey = model.linkDataArray.reduce<Record<string, Mapping>>(
      (acc, linkData) => {
        if (isMapping(linkData)) {
          acc[linkData.key] = linkData;
        }
        return acc;
      },
      {}
    );

    return model.linkDataArray.filter(
      (mapping): mapping is RelationshipMapping =>
        Boolean(
          isRelationshipMapping(mapping) &&
            mapping.relationshipIri === data.value &&
            ((mapping.from &&
              mappingsByKey[mapping.from]?.to === data.from &&
              mapping.toClassIri === toIri &&
              !mapping.toPrimaryIdentifier?.parts.length) ||
              (mapping.to &&
                mappingsByKey[mapping.to]?.to === data.to &&
                mapping.fromClassIri === fromIri &&
                !mapping.fromPrimaryIdentifier?.parts.length))
        )
    );
  };
